import React, { useEffect, useState,useContext } from 'react';
import { Grid, TextField, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { LabelsContext } from '../context/LabelsContext';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { formatDate, formatNumber, localeMap } from '../utils/formatterUtils'; // Import format utilities and localeMap
import config from '../config/config'; // Import config file

const fetchPriorities = async () => {
  // Simulate fetching priorities from an endpoint
  return ['Low', 'Medium', 'High', 'Critical'];
};

const fetchRiskLevels = async () => {
  // Simulate fetching risk levels from an endpoint
  return ['Low', 'Medium', 'High'];
};

const ProjectFormFields = ({ project, companies, locations, projectTypes, handleInputChange, handleCompanyChange, handleLocationChange }) => {
  const [priorities, setPriorities] = useState([]);
  const [riskLevels, setRiskLevels] = useState([]);
  const [dateError, setDateError] = useState('');

   // Consume LabelsContext
   const labels = useContext(LabelsContext);

  useEffect(() => {
    const fetchData = async () => {
      const prioritiesData = await fetchPriorities();
      setPriorities(prioritiesData);
      const riskLevelsData = await fetchRiskLevels();
      setRiskLevels(riskLevelsData);
    };
    fetchData();
  }, []);

  const validateDates = (startDate, plannedCompletionDate, actualCompletionDate) => {
    if (startDate && plannedCompletionDate && startDate > plannedCompletionDate) {
      setDateError('Start date should be less than planned completion date.');
      return false;
    }
    if (startDate && actualCompletionDate && startDate > actualCompletionDate) {
      setDateError('Start date should be less than actual completion date.');
      return false;
    }
    setDateError('');
    return true;
  };

  const handleDateChange = (name, date) => {
    const updatedProject = { ...project, [name]: date };
    const isValid = validateDates(updatedProject.start_date, updatedProject.planned_completion_date, updatedProject.actual_completion_date);
    if (isValid) {
      handleInputChange({ target: { name, value: date } });
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
      <FormControl variant="outlined" fullWidth>
  <InputLabel style={{ fontSize: 14 }}>{labels?.projects?.company_id || "Company Name"} </InputLabel>
  <Select
    value={project.company_id?.companyId || ''} // Ensure this matches the value in <MenuItem>
    onChange={(event) => {
      const selectedCompany = companies.find(company => company.companyId === event.target.value); // Match companyId
      handleCompanyChange(event, selectedCompany); // Pass the selected company to the handler
    }}
    label={labels?.projects?.company_id || "Company Name"}
    name="company_id"
    style={{ fontSize: 14 }}
  >
    {companies.map((company) => (
      <MenuItem key={company.companyId} value={company.companyId} style={{ fontSize: 14 }}> {/* Use companyId as key and value */}
        {company.companyName} {/* Display companyName */}
      </MenuItem>
    ))}
  </Select>
</FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={labels.projects.project_name || 'Project Name'} 
          variant="outlined"
          fullWidth
          name="project_name"
          value={project.project_name}
          onChange={handleInputChange}
          InputLabelProps={{ style: { fontSize: 14 } }}
          InputProps={{ style: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel style={{ fontSize: 14 }}>{labels.projects.location_id || "Location"} </InputLabel>
          <Select
            value={project.location_id?.id || ''}
            onChange={(event) => {
              const selectedLocation = locations.find(location => location.id === event.target.value);
              handleLocationChange(event, selectedLocation);
            }}
            label={labels.projects.location_id || "Location"}
            name="location_id"
            style={{ fontSize: 14 }}
          >
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id} style={{ fontSize: 14 }}>
                {location.locationName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={labels.projects.project_manager || "Project Manager"} 
          variant="outlined"
          fullWidth
          name="project_manager"
          value={project.project_manager || ''}
          onChange={handleInputChange}
          InputLabelProps={{ style: { fontSize: 14 } }}
          InputProps={{ style: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel style={{ fontSize: 14 }}>{labels.projects.project_type || "Project Type"} </InputLabel>
          <Select
            value={project.project_type || ''}
            onChange={handleInputChange}
            label={labels.projects.project_type || "Project Type"}
            name="project_type"
            style={{ fontSize: 14 }}
          >
            {projectTypes.map((type) => (
              <MenuItem key={type.project_type_id} value={type.project_type_id} style={{ fontSize: 14 }}>
                {type.project_type_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel style={{ fontSize: 14 }}>{labels.projects.priority || "Priority"} </InputLabel>
          <Select
            value={project.priority || ''}
            onChange={handleInputChange}
            label={labels.projects.priority || "Priority"}
            name="priority"
            style={{ fontSize: 14 }}
          >
            {priorities.map((priority) => (
              <MenuItem key={priority} value={priority} style={{ fontSize: 14 }}>
                {priority}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel style={{ fontSize: 14 }}>{labels.projects.risk_level || "Risk Level"} </InputLabel>
          <Select
            value={project.risk_level || ''}
            onChange={handleInputChange}
            label={labels.projects.risk_level || "Risk Level"}
            name="risk_level"
            style={{ fontSize: 14 }}
          >
            {riskLevels.map((riskLevel) => (
              <MenuItem key={riskLevel} value={riskLevel} style={{ fontSize: 14 }}>
                {riskLevel}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={labels.projects.budget || "Budget"}
          variant="outlined"
          fullWidth
          name="budget"
          value={formatNumber(project.budget) || ''} // Format number
          onChange={handleInputChange}
          InputLabelProps={{ style: { fontSize: 14 } }}
          InputProps={{ style: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={labels.projects.description || "Description"}
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          name="description"
          value={project.description}
          onChange={handleInputChange}
          InputLabelProps={{ style: { fontSize: 14 } }}
          InputProps={{ style: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label={labels.projects.deliverables || "Deliverables"}
          variant="outlined"
          fullWidth
          name="deliverables"
          value={project.deliverables || ''}
          onChange={handleInputChange}
          InputLabelProps={{ style: { fontSize: 14 } }}
          InputProps={{ style: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
          <DatePicker
            label={labels.projects.start_date ||  "Start Date"}
            value={project.start_date}
            onChange={(date) => handleDateChange('start_date', date)}
            renderInput={(params) => <TextField {...params} fullWidth InputLabelProps={{ style: { fontSize: 14 } }} InputProps={{ style: { fontSize: 14 } }} />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} sm={6}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
          <DatePicker
            label={labels.projects.planned_completion_date || "Planned Completion Date"} 
            value={project.planned_completion_date}
            onChange={(date) => handleDateChange('planned_completion_date', date)}
            renderInput={(params) => <TextField {...params} fullWidth InputLabelProps={{ style: { fontSize: 14 } }} InputProps={{ style: { fontSize: 14 } }} />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} sm={6}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
          <DatePicker
            label={labels.projects.actual_completion_date || "Actual Completion Date"} 
            value={project.actual_completion_date}
            onChange={(date) => handleDateChange('actual_completion_date', date)}
            renderInput={(params) => <TextField {...params} fullWidth InputLabelProps={{ style: { fontSize: 14 } }} InputProps={{ style: { fontSize: 14 } }} />}
          />
        </LocalizationProvider>
      </Grid>
      {dateError && (
        <Grid item xs={12}>
          <Typography color="error" variant="body2">
            {dateError}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField
          label={labels.projects.estimated_cost || "Estimated Cost"} 
          variant="outlined"
          fullWidth
          name="estimated_cost"
          value={formatNumber(project.estimated_cost) || ''} // Format number
          onChange={handleInputChange}
          InputLabelProps={{ style: { fontSize: 14 } }}
          InputProps={{ style: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={labels.projects.actual_cost || "Actual Cost"} 
          variant="outlined"
          fullWidth
          name="actual_cost"
          value={formatNumber(project.actual_cost) || ''} // Format number
          onChange={handleInputChange}
          InputLabelProps={{ style: { fontSize: 14 } }}
          InputProps={{ style: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={labels.projects.actual_hours || "Actual Hours"} 
          variant="outlined"
          fullWidth
          name="actual_hours"
          value={formatNumber(project.actual_hours) || ''} // Format number
          onChange={handleInputChange}
          InputLabelProps={{ style: { fontSize: 14 } }}
          InputProps={{ style: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label={labels.projects.estimated_hours || "Estimated Hours"} 
          variant="outlined"
          fullWidth
          name="estimated_hours"
          value={formatNumber(project.estimated_hours) || ''} // Format number
          onChange={handleInputChange}
          InputLabelProps={{ style: { fontSize: 14 } }}
          InputProps={{ style: { fontSize: 14 } }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel style={{ fontSize: 14 }}>{labels.projects.status || "Status"} </InputLabel>
          <Select
            value={project.status}
            onChange={handleInputChange}
            label={labels.projects.status || "Status"}
            name="status"
            style={{ fontSize: 14 }}
          >
            <MenuItem value="Ongoing" style={{ fontSize: 14 }}>Ongoing</MenuItem>
            <MenuItem value="Completed" style={{ fontSize: 14 }}>Completed</MenuItem>
            <MenuItem value="Delayed" style={{ fontSize: 14 }}>Delayed</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default ProjectFormFields;